/** Ensures hex colors have a `#` prefix. */
export const prefixHexColor = <T extends string | null | undefined>(color: T): T => {
  if (!color) return color;
  return (color.startsWith('#') ? color : `#${color}`) as T;
};

/** Removes the `#` prefix from a hex color string. */
export const stripHexPrefix = (color: string): string => {
  if (!color) return color;
  return color.startsWith('#') ? color.substring(1) : color;
};

/** Expands shorthand 3-digit hex to 6-digit (e.g. `"F0A"` → `"FF00AA"`). */
export const expandShorthandHexColor = (color: string): string => {
  color = stripHexPrefix(color);
  if (color.length === 3) {
    return color
      .split('')
      .map(c => c + c)
      .join('');
  }
  return color;
};

function hexToRgb(hex: string): [number, number, number] {
  const cleaned = stripHexPrefix(hex);
  const num = parseInt(cleaned, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map(c =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio check between two hex colors. */
export function isContrastReadable(
  foreground: string = '#fff',
  background: string = '#000',
  ratio: number = 2.6
): boolean {
  const [r1, g1, b1] = hexToRgb(foreground);
  const [r2, g2, b2] = hexToRgb(background);
  const l1 = relativeLuminance(r1, g1, b1);
  const l2 = relativeLuminance(r2, g2, b2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05) > ratio;
}
